import { useState } from 'react'
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, downloadCsv, tooltipStyle, typeLabel } from './reportShared'
import { useProcedureTurnaroundReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { windowLabel, type ReportTimeFrame } from '@/lib/reportRange'

/**
 * Report 1.4 — procedure turnaround time by procedure type.
 *
 * ## What the window means here
 *
 * It bounds the COMPLETION, not the start. A turnaround figure describes
 * finished work, so a run that began in July and closed this month belongs to
 * this month — and the subtitle says "completed in" rather than leaving a
 * reader to assume the usual.
 *
 * ## Why the median leads
 *
 * One run held open over a weekend drags a mean by days, so the headline is
 * the median: what a typical run took. The average sits beside it, and when
 * the two disagree that gap IS the finding — the spread bar underneath shows
 * fastest, median, p90 and slowest so the shape is visible rather than implied.
 *
 * Horizontal bars per type, not a pie: comparing four durations is a length
 * comparison, and the names fit on the axis instead of in a legend.
 */
const BAR = '#2f6f9f'
const SLOW = '#eb6834'

/** Hours as something a person says out loud. */
function humanHours(hours: number | null): string {
  if (hours === null) return '—'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 48) return `${Math.round(hours * 10) / 10}h`
  const days = hours / 24
  return `${Math.round(days * 10) / 10}d`
}

const PAGE_SIZE = 20

export function ProcedureTurnaroundReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useProcedureTurnaroundReport(frame, filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const byType = report.byType.map((t) => ({
    name: typeLabel(t.type),
    median: t.medianHours ?? 0,
    label: humanHours(t.medianHours),
    count: t.count,
    average: t.averageHours,
    p90: t.p90Hours,
  }))

  const pageCount = Math.max(1, Math.ceil(report.rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.rows.slice(start, start + PAGE_SIZE)
  // The slowest run in the set, so the one bar worth noticing is the one that
  // looks different.
  const slowest = byType.reduce((m, t) => Math.max(m, t.median), 0)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Started', 'Completed', 'Hours', 'Procedure type', 'Template', 'Work order', 'Patient MRN'],
        report.rows.map((r) => [
          r.startedOn,
          r.completedOn,
          r.hours,
          typeLabel(r.type),
          r.templateName,
          r.workOrderNumber,
          r.patientMrn,
        ]),
      ),
      reportFileName('procedures-turnaround', report.window),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <ReportSection
        title='Turnaround time'
        subtitle={`Completed in ${windowLabel({ from: report.window.from, to: report.window.to })}`}
      >
        <div className='flex flex-col gap-4 p-4'>
          <div className='flex flex-wrap items-end gap-x-8 gap-y-3'>
            <div>
              <p className='text-5xl font-semibold tabular-nums' style={{ color: BAR }}>
                {humanHours(report.overall.medianHours)}
              </p>
              <p className='mt-1 text-sm text-muted-foreground'>
                {report.overall.count === 0
                  ? 'Nothing completed in this window'
                  : `median across ${report.overall.count} completed`}
              </p>
            </div>
            <dl className='flex flex-wrap gap-x-6 gap-y-2'>
              <Stat label='Average' value={humanHours(report.overall.averageHours)} />
              <Stat label='Fastest' value={humanHours(report.overall.minHours)} />
              <Stat label='90th percentile' value={humanHours(report.overall.p90Hours)} />
              <Stat label='Slowest' value={humanHours(report.overall.maxHours)} />
            </dl>
          </div>

          {/* Reported apart, never inside the figures above — the same rule
              1.3 applies to a run with no due date. */}
          {report.undatable > 0 && (
            <p className='text-xs text-muted-foreground'>
              {report.undatable} completed without a usable timestamp, excluded from these figures.
            </p>
          )}
        </div>
      </ReportSection>

      {byType.length > 0 && (
        <ReportSection title='Median by procedure type'>
          <div className='p-4'>
            <ResponsiveContainer width='100%' height={Math.max(140, byType.length * 46)}>
              <BarChart data={byType} layout='vertical' margin={{ left: 8, right: 56, top: 4, bottom: 4 }}>
                <XAxis type='number' tick={{ fontSize: 11 }} hide />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={140}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(_v, _n, item) => {
                    const t = item.payload as (typeof byType)[number]
                    return [
                      `median ${t.label} · avg ${humanHours(t.average)} · p90 ${humanHours(t.p90)} · ${t.count} runs`,
                      'Turnaround',
                    ]
                  }}
                />
                <Bar dataKey='median' radius={[0, 4, 4, 0]} barSize={22}>
                  {byType.map((t) => (
                    <Cell key={t.name} fill={t.median === slowest && byType.length > 1 ? SLOW : BAR} />
                  ))}
                  {/* Direct-labelled, so the axis is a convenience rather than
                      the only way to read a value. */}
                  <LabelList dataKey='label' position='right' style={{ fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ReportSection>
      )}

      <ReportSection title='Slowest first' onExport={report.rows.length > 0 ? exportCsv : undefined}>
        {report.rows.length === 0 ? (
          <p className='px-4 py-6 text-xs text-muted-foreground'>Nothing completed in this window.</p>
        ) : (
          <>
            <div className='overflow-x-persistent'>
              <table className='w-full text-xs'>
                <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Started</th>
                    <th className='px-3 py-2 font-medium'>Completed</th>
                    <th className='px-3 py-2 text-right font-medium'>Turnaround</th>
                    <th className='px-3 py-2 font-medium'>Type</th>
                    <th className='px-3 py-2 font-medium'>Template</th>
                    <th className='px-3 py-2 font-medium'>Work order</th>
                    <th className='px-3 py-2 font-medium'>Patient</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className='border-b last:border-0'>
                      <td className='px-3 py-1.5 tabular-nums'>{r.startedOn}</td>
                      <td className='px-3 py-1.5 tabular-nums'>{r.completedOn}</td>
                      <td className='px-3 py-1.5 text-right font-medium tabular-nums'>
                        {humanHours(r.hours)}
                      </td>
                      <td className='px-3 py-1.5'>{typeLabel(r.type)}</td>
                      <td className='px-3 py-1.5'>{r.templateName}</td>
                      <td className='px-3 py-1.5'>{r.workOrderNumber ?? '—'}</td>
                      <td className='px-3 py-1.5'>{r.patientMrn ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ReportPager
              page={current}
              pageCount={pageCount}
              from={start + 1}
              to={start + rows.length}
              total={report.rows.length}
              onPage={setPage}
            />
          </>
        )}
      </ReportSection>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className='text-xs text-muted-foreground'>{label}</dt>
      <dd className='text-xl font-semibold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
